import React, { useEffect, useMemo } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { useDispatch, useSelector } from 'react-redux';
import { auth } from '../firebase';
import { ThemeContext } from './ThemeContext';
import { BrandingConfig } from './types';
import { defaultBranding } from './defaultTheme';
import {
  userAuthenticated,
  userLoggedOut,
  selectBranding,
  selectBrandingIsLoading,
  selectIsCustomBranding,
} from '../store/slices/brandingSlice';

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Apply CSS custom properties to document root
 */
function applyCSSVariables(branding: BrandingConfig) {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', branding.primaryColor);
  root.style.setProperty('--color-primary-hover', branding.primaryColorHover);
  root.style.setProperty('--color-primary-active', branding.primaryColorActive);
  root.style.setProperty('--color-secondary', branding.secondaryColor);
  root.style.setProperty('--color-secondary-hover', branding.secondaryColorHover);
  root.style.setProperty('--color-background', branding.backgroundColor);
  root.style.setProperty('--color-surface', branding.surfaceColor);
  root.style.setProperty('--color-text', branding.textColor);
  root.style.setProperty('--color-text-muted', branding.textColorMuted);
  if (branding.fontFamily) {
    root.style.setProperty('--font-family', branding.fontFamily);
  }
  if (branding.borderRadius) {
    root.style.setProperty('--border-radius', branding.borderRadius);
  }
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const branding = useSelector(selectBranding);
  const isLoading = useSelector(selectBrandingIsLoading);
  const isCustomBranding = useSelector(selectIsCustomBranding);

  // Listen to Firebase auth state and dispatch actions
  useEffect(() => {
    if (!auth) {
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User authenticated - dispatch action to trigger saga
        dispatch(userAuthenticated({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
        }));
      } else {
        // User logged out - reset branding
        dispatch(userLoggedOut());
        applyCSSVariables(defaultBranding);
      }
    });

    return unsubscribe;
  }, [dispatch]);

  // Apply CSS variables when branding changes
  useEffect(() => {
    applyCSSVariables(branding);
  }, [branding]);

  // Apply default CSS variables on mount
  useEffect(() => {
    applyCSSVariables(defaultBranding);
  }, []);

  const contextValue = useMemo(
    () => ({
      branding,
      isLoading,
      isCustomBranding,
    }),
    [branding, isLoading, isCustomBranding]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
